import { useState, useEffect, useCallback } from "react";
import { Loader2, Pencil, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";

export default function MesDonneesTab({ parcelId }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    authFetch(`${API_BASE}/agro/parcel/${parcelId}/custom-data`)
      .then(r => r.json()).then(d => { setData(d); setLoaded(true); }).catch(() => setLoaded(true));
  }, [parcelId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/agro/parcel/${parcelId}/custom-data`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, parcel_id: parcelId }),
      });
      if (!res.ok) throw new Error();
      toast.success("Donnees sauvegardees");
    } catch { toast.error("Erreur de sauvegarde"); }
    setSaving(false);
  };

  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const FIELDS = [
    { key: "soil_type", label: "Type de sol", type: "select", options: ["Andosol", "Ferralitique", "Brun", "Vertisol", "Alluvial"] },
    { key: "ph_sol", label: "pH sol", type: "number", step: "0.1" },
    { key: "organic_matter_pct", label: "Matiere organique (%)", type: "number", step: "0.1" },
    { key: "nitrogen_ppm", label: "Azote (ppm)", type: "number" },
    { key: "phosphorus_ppm", label: "Phosphore (ppm)", type: "number" },
    { key: "potassium_ppm", label: "Potassium (ppm)", type: "number" },
    { key: "irrigation_type", label: "Irrigation", type: "select", options: ["Aucune", "Goutte-a-goutte", "Aspersion", "Micro-aspersion", "Gravitaire"] },
    { key: "drainage_quality", label: "Drainage", type: "select", options: ["Bon", "Moyen", "Mauvais"] },
    { key: "slope_pct", label: "Pente (%)", type: "number" },
    { key: "last_analysis_date", label: "Date derniere analyse sol", type: "date" },
    { key: "notes", label: "Notes", type: "text" },
  ];

  if (!loaded) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>;

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3" data-testid="mes-donnees-tab">
        <div className="flex items-center gap-2">
          <Pencil className="w-3.5 h-3.5" style={{ color: "#2D6A4F" }} />
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#2D6A4F" }}>Donnees agronomiques terrain</p>
        </div>
        <p className="text-[10px]" style={{ color: "#7A7A7A" }}>Ces donnees seront integrees dans l'analyse IA.</p>
        <div className="grid grid-cols-2 gap-2">
          {FIELDS.map(f => (
            <div key={f.key}>
              <Label className="text-[10px] font-medium" style={{ color: "#4A4A4A" }}>{f.label}</Label>
              {f.type === "select" ? (
                <Select value={data[f.key] || ""} onValueChange={v => set(f.key, v)}>
                  <SelectTrigger className="h-8 text-xs mt-0.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="z-[9999]">{f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={f.type} step={f.step} value={data[f.key] || ""} onChange={e => set(f.key, f.type === "number" ? (parseFloat(e.target.value) || null) : e.target.value)}
                  className="h-8 text-xs mt-0.5" placeholder="—" data-testid={`custom-field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={saving} className="w-full h-9 text-xs font-semibold rounded-lg text-white gap-1.5" style={{ background: "#2D6A4F" }} data-testid="save-custom-agro-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Sauvegarder
        </Button>
      </div>
    </ScrollArea>
  );
}
